// MachineMarketplace.jsx
import React, { useMemo, useState } from "react";
import {
    Search,
    SlidersHorizontal,
    Cpu,
    Calendar,
    Info,
    X,
    Clock,
    MapPin,
    Building,
    Wrench,
    Check,
    ExternalLink,
    Filter,
    XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const CATEGORIES = ["All", "3D Printing", "CNC", "Laser Cutting", "PCB", "Moulding", "Robotics"];

const MACHINES = [
    {
        id: "M-3DP-01",
        name: "Industrial FDM 3D Printer",
        category: "3D Printing",
        make: "Stratasys F770",
        centre: "APIC Visakhapatnam",
        rate: 850,
        status: "Available",
        description: "High-precision FDM 3D printer with large build volume. Ideal for prototyping and production parts.",
        specs: ["Build Volume: 300x300x300mm", "Layer Resolution: 50 microns", "Material: PLA, ABS, PETG"],
    },
    {
        id: "M-CNC-05",
        name: "5-Axis CNC Machining Centre",
        category: "CNC",
        make: "DMG Mori DMU 50",
        centre: "APIC Vijayawada",
        rate: 2400,
        status: "Running",
        description: "Advanced 5-axis machining center for complex geometries. Perfect for aerospace and automotive parts.",
        specs: ["Travel: 500x450x400mm", "Spindle Speed: 18,000 RPM", "Tool Magazine: 30 tools"],
    },
    {
        id: "M-LSR-02",
        name: "Fiber Laser Cutter 3kW",
        category: "Laser Cutting",
        make: "Trumpf TruLaser 3030",
        centre: "APIC Tirupati",
        rate: 1600,
        status: "Available",
        description: "High-power fiber laser cutter for precision cutting of metals up to 25mm thickness.",
        specs: ["Cutting Area: 3000x1500mm", "Max Thickness: 25mm", "Cutting Speed: 40m/min"],
    },
    {
        id: "M-PCB-03",
        name: "PCB Prototyping Mill",
        category: "PCB",
        make: "LPKF ProtoMat S104",
        centre: "APIC Kurnool",
        rate: 1100,
        status: "Available",
        description: "Precision PCB prototyping mill for rapid circuit board development.",
        specs: ["Working Area: 300x200mm", "Resolution: 1 micron", "Spindle Speed: 60,000 RPM"],
    },
    {
        id: "M-MLD-01",
        name: "Injection Moulding Unit",
        category: "Moulding",
        make: "Arburg Allrounder 270",
        centre: "APIC Guntur",
        rate: 3200,
        status: "Maintenance",
        description: "Industrial injection molding machine for high-volume production of plastic parts.",
        specs: ["Clamping Force: 1000kN", "Shot Size: 50g", "Cycle Time: 3 seconds"],
    },
    {
        id: "M-ROB-04",
        name: "6-Axis Robotic Arm Cell",
        category: "Robotics",
        make: "KUKA KR 10 R1100",
        centre: "APIC Nellore",
        rate: 1900,
        status: "Running",
        description: "Versatile 6-axis robotic arm for welding, assembly, and material handling.",
        specs: ["Reach: 1100mm", "Payload: 10kg", "Repeatability: ±0.05mm"],
    },
];

const STATUS_STYLES = {
    Available: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Running: "bg-blue-100 text-blue-800 border-blue-200",
    Maintenance: "bg-amber-100 text-amber-800 border-amber-200",
};

const STATUS_ICONS = {
    Available: Check,
    Running: Clock,
    Maintenance: Wrench,
};

function MachineCard({ machine, onBook, onDetails }) {
    const StatusIcon = STATUS_ICONS[machine.status] || Info;

    return (
        <div className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-indigo-200 hover:-translate-y-1">
            <div className="mb-4 flex items-start justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50">
                    <Cpu className="h-6 w-6" />
                </span>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLES[machine.status]}`}>
                    <StatusIcon className="h-3 w-3" />
                    {machine.status}
                </span>
            </div>

            <h3 className="text-base font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                {machine.name}
            </h3>
            <p className="mt-0.5 text-xs font-mono text-indigo-500">{machine.id}</p>

            <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-slate-500">Technology</span>
                    <span className="font-medium text-slate-700">{machine.category}</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-slate-500">Make</span>
                    <span className="font-medium text-slate-700">{machine.make}</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-slate-500">Centre</span>
                    <span className="font-medium text-slate-700">{machine.centre}</span>
                </div>
                <div className="flex items-center justify-between pt-1">
                    <span className="text-slate-500">Hourly rate</span>
                    <span className="text-lg font-bold text-indigo-600">₹{machine.rate.toLocaleString("en-IN")}<span className="text-sm font-normal text-slate-500">/hr</span></span>
                </div>
            </div>

            <div className="mt-5 flex items-center gap-2">
                <button 
                    onClick={() => onBook(machine)}
                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200/50 transition-all hover:scale-[1.02] hover:shadow-indigo-300/70"
                >
                    <Calendar className="h-4 w-4" />
                    Book Now
                </button>
                <button 
                    onClick={() => onDetails(machine.id)}
                    className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all hover:bg-indigo-50 hover:border-indigo-200"
                >
                    <ExternalLink className="h-4 w-4" />
                    Details
                </button>
            </div>
        </div>
    );
}

function BookingPopup({ machine, onClose, onConfirm }) {
    const [bookingDate, setBookingDate] = useState("");
    const [hours, setHours] = useState(1);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");

    const totalAmount = machine ? machine.rate * hours : 0;

    const handleSubmit = (e) => {
        e.preventDefault();
        onConfirm({
            machine: machine.name,
            machineId: machine.id,
            date: bookingDate,
            hours,
            name,
            email,
            total: totalAmount,
        });
    };

    if (!machine) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-lg mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-white">Book Machine</h2>
                        <button 
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                    <p className="text-sm text-white/80 mt-1">{machine.name} • {machine.id}</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                Full Name
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                                placeholder="John Doe"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                                placeholder="john@example.com"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                            Booking Date
                        </label>
                        <input
                            type="date"
                            value={bookingDate}
                            onChange={(e) => setBookingDate(e.target.value)}
                            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                            Hours Required
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="range"
                                min="1"
                                max="24"
                                value={hours}
                                onChange={(e) => setHours(Number(e.target.value))}
                                className="flex-1 h-2 rounded-lg bg-indigo-100 appearance-none cursor-pointer accent-indigo-600"
                            />
                            <span className="text-lg font-bold text-indigo-600 min-w-[60px] text-center">
                                {hours}h
                            </span>
                        </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">Rate per hour</span>
                            <span className="font-semibold text-slate-800">₹{machine.rate.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm border-t border-slate-200 pt-2">
                            <span className="font-semibold text-slate-800">Total Amount</span>
                            <span className="text-xl font-bold text-indigo-600">₹{totalAmount.toLocaleString("en-IN")}</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-indigo-200/50 hover:shadow-indigo-300/70 transition-all hover:scale-[1.01]"
                    >
                        Confirm Booking
                    </button>
                </form>
            </div>
        </div>
    );
}

export default function MachineMarketplace() {
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");
    const [bookingMachine, setBookingMachine] = useState(null);
    const [showBookingPopup, setShowBookingPopup] = useState(false);

    const filtered = useMemo(() => {
        return MACHINES.filter((m) => {
            const matchesQuery =
                query.trim() === "" ||
                [m.name, m.make, m.centre, m.category, m.id].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesCategory = category === "All" || m.category === category;
            return matchesQuery && matchesCategory;
        });
    }, [query, category]);

    const handleBook = (machine) => {
        setBookingMachine(machine);
        setShowBookingPopup(true);
    };

    const handleBookingConfirm = (bookingData) => {
        console.log("Booking confirmed:", bookingData);
        alert(`✅ Booking confirmed for ${bookingData.machine}!\nTotal: ₹${bookingData.total.toLocaleString("en-IN")}`);
        setShowBookingPopup(false);
        setBookingMachine(null);
    };

    const handleDetails = (machineId) => {
        navigate(`/machine/${machineId}`);
    };

    // Get active filter count
    const activeFilterCount = category !== "All" ? 1 : 0;

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-[#0f1c3f] via-[#1a2e6b] to-[#5b21b6] px-4 sm:px-8 pb-16 pt-8 text-white overflow-hidden">
                {/* Premium decorative elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
                
                <div className="relative max-w-7xl mx-auto">
  <div className="rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 p-4 shadow-2xl">
    <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">

      {/* Search Bar */}
      <div className="relative min-w-[320px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search machine, technology or make..."
          className="w-full rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 py-2.5 pl-9 pr-3 text-sm outline-none focus:bg-white/20 focus:border-white/40 transition"
        />
      </div>

      {/* Clear Button */}
      {query && (
        <button
          onClick={() => setQuery("")}
          className="flex items-center gap-1 rounded-lg bg-white/10 px-3 py-2 text-white hover:bg-white/20 whitespace-nowrap"
        >
          <XCircle className="h-4 w-4" />
          Clear
        </button>
      )}

      {/* Category Filters */}
      {CATEGORIES.map((c) => (
        <button
          key={c}
          onClick={() => setCategory(c)}
          className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all ${
            category === c
              ? "bg-white text-indigo-700 shadow-lg"
              : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
          }`}
        >
          {c}
        </button>
      ))}

      {/* Active Filter Count */}
      {activeFilterCount > 0 && (
        <span className="whitespace-nowrap rounded-full bg-indigo-600 px-3 py-2 text-sm text-white">
          {activeFilterCount} Selected
        </span>
      )}
    </div>
  </div>
</div>
            </div>

            {/* Machine Grid */}
            <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-12 -mt-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
                    <p className="text-sm text-slate-500 font-medium">
                        <span className="text-indigo-600 font-bold">{filtered.length}</span> of {MACHINES.length} machines
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
                        <span>Available</span>
                        <span className="inline-block w-2 h-2 rounded-full bg-blue-400 ml-2"></span>
                        <span>Running</span>
                        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 ml-2"></span>
                        <span>Maintenance</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
                    {filtered.map((m) => (
                        <MachineCard 
                            key={m.id} 
                            machine={m} 
                            onBook={handleBook}
                            onDetails={handleDetails}
                        />
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="mt-10 flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white/50 backdrop-blur-sm py-16 text-center">
                        <Info className="mb-3 h-8 w-8 text-slate-300" />
                        <p className="text-sm font-medium text-slate-500">No machines match your search.</p>
                        <button 
                            onClick={() => { setQuery(""); setCategory("All"); }}
                            className="mt-3 text-indigo-600 font-semibold text-sm hover:text-indigo-700 transition"
                        >
                            Clear all filters →
                        </button>
                    </div>
                )}
            </div>

            {/* Booking Popup */}
            {showBookingPopup && (
                <BookingPopup 
                    machine={bookingMachine}
                    onClose={() => { setShowBookingPopup(false); setBookingMachine(null); }}
                    onConfirm={handleBookingConfirm}
                />
            )}
        </div>
    );
}